import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
  updateDoc,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { firestore } from '@config/firebase';
import { PropertyInquiry, InquiryStatus } from '../types/inquiry.types';
import { getErrorMessage } from '@utils/errors';

export interface UseInquiriesState {
  inquiries: PropertyInquiry[];
  loading: boolean;
  error: string | null;
}

export const useInquiries = (landlordId?: string, statusFilter?: InquiryStatus) => {
  const [state, setState] = useState<UseInquiriesState>({
    inquiries: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!landlordId) {
      setState({
        inquiries: [],
        loading: false,
        error: null,
      });
      return;
    }

    let unsubscribe: Unsubscribe;

    try {
      let inquiriesQuery = query(
        collection(firestore, 'inquiries'),
        where('landlordId', '==', landlordId),
        orderBy('createdAt', 'desc')
      );

      if (statusFilter) {
        inquiriesQuery = query(
          collection(firestore, 'inquiries'),
          where('landlordId', '==', landlordId),
          where('status', '==', statusFilter),
          orderBy('createdAt', 'desc')
        );
      }

      unsubscribe = onSnapshot(
        inquiriesQuery,
        (snapshot) => {
          const inquiriesData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as PropertyInquiry[];

          setState({
            inquiries: inquiriesData,
            loading: false,
            error: null,
          });
        },
        (error) => {
          console.error('Error fetching inquiries:', error);
          setState((prev) => ({
            ...prev,
            loading: false,
            error: error.message,
          }));
        }
      );
    } catch (error: unknown) {
      console.error('Error setting up inquiries listener:', error);
      setState({
        inquiries: [],
        loading: false,
        error: getErrorMessage(error),
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [landlordId, statusFilter]);

  const updateInquiryStatus = async (
    inquiryId: string,
    newStatus: InquiryStatus
  ): Promise<void> => {
    try {
      const inquiryRef = doc(firestore, 'inquiries', inquiryId);
      const updateData: Partial<PropertyInquiry> & { updatedAt: Timestamp; respondedAt?: Timestamp } = {
        status: newStatus,
        updatedAt: Timestamp.now(),
      };

      if (newStatus === 'responded' || newStatus === 'scheduled' || newStatus === 'declined' || newStatus === 'accepted') {
        updateData.respondedAt = Timestamp.now();
      }

      await updateDoc(inquiryRef, updateData);
    } catch (error: unknown) {
      console.error('Error updating inquiry status:', error);
      throw new Error('Failed to update inquiry status');
    }
  };

  return {
    ...state,
    updateInquiryStatus,
  };
};
